import { useState, useEffect, useRef, useCallback } from 'react';
import { rmmAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, TerminalSquare, FolderOpen, Ticket, Sparkles, Wrench, ScreenShare, Film, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { fmtWhen } from './rmmUtils';
import { RemoteDesktop } from './RemoteDesktop';
import { RecordingPlayer } from './RecordingPlayer';

const err = (e, m) => toast.error(e.response?.data?.detail || m);
const WIN_TOOLS = ['event_logs', 'scheduled_tasks', 'local_users', 'network_adapters', 'printers', 'volumes', 'update_status', 'device_info'];

// Phase 7/8 tools panel embedded in DeviceDetail
export const RemoteTools = ({ device, isAdmin }) => {
  const [session, setSession] = useState(null);
  const [cmdText, setCmdText] = useState('');
  const [transcript, setTranscript] = useState([]);
  const [path, setPath] = useState('C:\\');
  const [fileOut, setFileOut] = useState(null);
  const [tool, setTool] = useState('event_logs');
  const [toolOut, setToolOut] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [remoteOpen, setRemoteOpen] = useState(false);
  const [recordEnabled, setRecordEnabled] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [playRec, setPlayRec] = useState(null);
  const pollRef = useRef(null);

  const loadRecordings = useCallback(() => {
    rmmAPI.remoteRecordings(device.id).then((r) => setRecordings(r.data)).catch(() => {});
  }, [device.id]);

  const refreshTranscript = useCallback((sid) => {
    rmmAPI.sessionTranscript(sid).then((r) => setTranscript(r.data.commands || [])).catch(() => {});
  }, []);

  useEffect(() => {
    rmmAPI.deviceTickets(device.id).then((r) => setTickets(r.data)).catch(() => {});
    loadRecordings();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [device.id, loadRecordings]);

  const deleteRecording = (rid) => {
    if (!confirm('Delete this session recording permanently?')) return;
    rmmAPI.deleteRemoteRecording(rid).then(() => { toast.success('Recording deleted'); loadRecordings(); }).catch((e) => err(e, 'Delete failed'));
  };

  const startSession = () => {
    rmmAPI.startSession(device.id, 'Remote troubleshooting').then((r) => {
      setSession(r.data);
      refreshTranscript(r.data.id);
      pollRef.current = setInterval(() => refreshTranscript(r.data.id), 4000);
      toast.success('Remote console session started (audited)');
    }).catch((e) => err(e, 'Failed to start session'));
  };
  const endSession = () => {
    rmmAPI.endSession(session.id).then(() => { toast.success('Session ended'); setSession(null); clearInterval(pollRef.current); });
  };
  const exec = () => {
    if (!cmdText.trim()) return;
    rmmAPI.sessionExec(session.id, cmdText).then(() => { setCmdText(''); refreshTranscript(session.id); }).catch((e) => err(e, 'Exec failed'));
  };
  const waitForOutput = (cmdId, setter, parse = true, tries = 15) => {
    const iv = setInterval(() => {
      rmmAPI.listCommands(device.id).then((r) => {
        const c = r.data.find((x) => x.id === cmdId);
        if (c && ['completed', 'failed', 'expired'].includes(c.status)) {
          clearInterval(iv);
          let out = c.output || `(${c.status} — no output)`;
          if (parse) { try { out = JSON.stringify(JSON.parse(out), null, 1); } catch { /* raw */ } }
          setter({ status: c.status, output: out });
        }
      });
      if (--tries <= 0) clearInterval(iv);
    }, 3000);
  };
  const runTool = () => {
    setToolOut({ status: 'waiting', output: 'Queued — waiting for agent…' });
    rmmAPI.winTool(device.id, tool).then((r) => waitForOutput(r.data.id, setToolOut)).catch((e) => err(e, 'Failed'));
  };
  const listFiles = () => {
    setFileOut({ status: 'waiting', output: 'Queued — waiting for agent…' });
    rmmAPI.fileOp(device.id, { op: 'file_list', path }).then((r) => waitForOutput(r.data.id, setFileOut)).catch((e) => err(e, 'Failed'));
  };
  const aiAssist = () => {
    setAiLoading(true);
    rmmAPI.aiDeviceAssist(device.id).then((r) => setAi(r.data)).catch((e) => err(e, 'AI assist failed')).finally(() => setAiLoading(false));
  };

  return (
    <div className="space-y-3 pt-2 border-t border-slate-100" data-testid="rmm-remote-tools">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Wrench className="w-4 h-4 text-cyan-500" />Advanced Remote Tools</h3>
        <div className="flex items-center gap-2">
          {isAdmin && device.status === 'online' && (
            <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer" title="Record this session (encrypted, for compliance)">
              <input type="checkbox" checked={recordEnabled} onChange={(e) => setRecordEnabled(e.target.checked)} className="accent-red-500" data-testid="rmm-record-toggle" />
              Record
            </label>)}
          {isAdmin && device.status === 'online' && (
            <Button size="sm" className="rounded-full bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => setRemoteOpen(true)} data-testid="rmm-remote-control-btn">
              <ScreenShare className="w-3.5 h-3.5 mr-1" />Remote Control
            </Button>)}
          <Button size="sm" variant="outline" className="rounded-full text-violet-600 border-violet-200" onClick={aiAssist} disabled={aiLoading} data-testid="rmm-ai-device-btn">
            {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}AI Analysis</Button>
        </div>
      </div>
      {remoteOpen && <RemoteDesktop device={device} record={recordEnabled} onClose={() => { setRemoteOpen(false); setTimeout(loadRecordings, 1500); }} />}
      {playRec && <RecordingPlayer recording={playRec} onClose={() => setPlayRec(null)} />}
      {ai && (
        <div className="p-3 rounded-xl border border-violet-200 bg-violet-50/50 text-xs text-slate-700 whitespace-pre-wrap" data-testid="rmm-ai-device-output">
          <Badge className="bg-violet-100 text-violet-700 mb-1.5">AI-generated · verify before acting</Badge>
          <p>{ai.summary}</p>
        </div>)}

      {isAdmin && (
        <div className="p-3 rounded-xl border border-slate-200 bg-slate-900 space-y-2" data-testid="rmm-console">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-300 flex items-center gap-1.5"><TerminalSquare className="w-3.5 h-3.5" />Remote PowerShell Console</span>
            {session ? <Button size="sm" variant="destructive" className="h-6 text-xs" onClick={endSession} data-testid="rmm-session-end-btn">End session</Button>
              : <Button size="sm" className="h-6 text-xs bg-cyan-600" onClick={startSession} data-testid="rmm-session-start-btn">Start session</Button>}
          </div>
          {session && (
            <>
              <div className="max-h-48 overflow-y-auto font-mono text-[11px] space-y-1.5" data-testid="rmm-console-transcript">
                {transcript.map((c) => (
                  <div key={c.id}>
                    <p className="text-cyan-400">PS&gt; {c.params?.command_text}</p>
                    <p className={c.status === 'failed' ? 'text-red-400' : 'text-slate-300'}>
                      {c.output || (c.status === 'pending' || c.status === 'delivered' ? '… waiting for agent' : `(${c.status})`)}</p>
                  </div>))}
                {transcript.length === 0 && <p className="text-slate-500">Session active — agent polls every ~5s. Type a command below.</p>}
              </div>
              <div className="flex gap-2">
                <Input value={cmdText} onChange={(e) => setCmdText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && exec()}
                  placeholder="Get-Process | Select -First 5" className="h-8 bg-slate-800 border-slate-700 text-slate-100 font-mono text-xs" data-testid="rmm-console-input" />
                <Button size="sm" className="h-8 bg-cyan-600" onClick={exec} data-testid="rmm-console-run-btn">Run</Button>
              </div>
            </>)}
        </div>)}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="p-3 rounded-xl border border-slate-200 space-y-2">
          <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5 text-cyan-500" />Windows Management</span>
          <div className="flex gap-2">
            <Select value={tool} onValueChange={setTool}>
              <SelectTrigger className="h-8 bg-white text-xs" data-testid="rmm-wintool-select"><SelectValue /></SelectTrigger>
              <SelectContent>{WIN_TOOLS.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-8" onClick={runTool} data-testid="rmm-wintool-run-btn">Query</Button>
          </div>
          {toolOut && <pre className="text-[10px] bg-slate-50 border border-slate-200 rounded-lg p-2 max-h-40 overflow-auto whitespace-pre-wrap" data-testid="rmm-wintool-output">{toolOut.output}</pre>}
        </div>
        <div className="p-3 rounded-xl border border-slate-200 space-y-2">
          <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><FolderOpen className="w-3.5 h-3.5 text-cyan-500" />Remote Files</span>
          <div className="flex gap-2">
            <Input value={path} onChange={(e) => setPath(e.target.value)} className="h-8 bg-white text-xs font-mono" data-testid="rmm-files-path-input" />
            <Button size="sm" variant="outline" className="h-8" onClick={listFiles} data-testid="rmm-files-list-btn">Browse</Button>
          </div>
          {fileOut && <pre className="text-[10px] bg-slate-50 border border-slate-200 rounded-lg p-2 max-h-40 overflow-auto whitespace-pre-wrap" data-testid="rmm-files-output">{fileOut.output}</pre>}
          <p className="text-[10px] text-slate-400">Writes/deletes are admin-only and blocked on protected Windows paths. Every operation is audited.</p>
        </div>
      </div>

      {recordings.length > 0 && (
        <div className="p-3 rounded-xl border border-slate-200" data-testid="rmm-recordings-list">
          <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1.5"><Film className="w-3.5 h-3.5 text-violet-500" />Session Recordings <span className="text-slate-400 font-normal">· encrypted</span></span>
          {recordings.map((r) => (
            <div key={r.id} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-100 last:border-0" data-testid={`rmm-recording-${r.id}`}>
              <span className="text-slate-700 min-w-0 truncate">
                <b>{r.technician_name || 'Technician'}</b> · {fmtWhen(r.created_at)}
                <span className="text-slate-400"> · {Math.round((r.duration_ms || 0) / 1000)}s · {r.frame_count} frames</span>
              </span>
              <span className="flex gap-1.5 shrink-0">
                <Button size="sm" variant="outline" className="h-7 rounded-full text-violet-600 border-violet-200" onClick={() => setPlayRec(r)} data-testid={`rmm-recording-play-${r.id}`}>
                  <Play className="w-3 h-3 mr-1" />Play
                </Button>
                {isAdmin && (
                  <Button size="sm" variant="ghost" className="h-7 rounded-full text-red-500" onClick={() => deleteRecording(r.id)} data-testid={`rmm-recording-delete-${r.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>)}
              </span>
            </div>))}
        </div>)}

      {tickets.length > 0 && (
        <div className="p-3 rounded-xl border border-slate-200" data-testid="rmm-device-tickets">
          <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1.5"><Ticket className="w-3.5 h-3.5 text-cyan-500" />Linked Help Desk Tickets</span>
          {tickets.map((t) => (
            <div key={t.id} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0">
              <span className="text-slate-700"><b>{t.ticket_number}</b> {t.subject}</span>
              <span className="flex gap-1.5"><Badge variant="outline" className="text-[10px] capitalize">{t.priority}</Badge>
                <Badge className="bg-slate-100 text-slate-600 text-[10px] capitalize">{t.status}</Badge>
                <span className="text-slate-400">{fmtWhen(t.created_at)}</span></span>
            </div>))}
        </div>)}
    </div>
  );
};
